import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Calendar, Activity, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function HeroLongevityFitness() {
  return <section id="herolongevityfitness" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border">
      {/* Decorative ambient background accents */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/2 -right-32 w-[32rem] h-[32rem] rounded-full bg-accent/30 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Text block */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <Badge variant="secondary" className="mb-6 px-3.5 py-1 text-xs sm:text-sm font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
              <Activity className="size-3.5 text-primary mr-1.5" />
              Longevity & Fitness
            </Badge>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.12] mb-6">
              Measure what matters for moving well, staying fit and ageing well
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-foreground/90 font-normal leading-relaxed mb-4">
              PhysAnalytica combines objective fitness, metabolic and movement assessment to help you understand important aspects of your physical capacity and make informed decisions about exercise, movement and long-term wellbeing.
            </p>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
              From aerobic fitness and metabolism to gait and balance, we measure key components of how your body performs today and help you identify areas worth working on.
            </p>

            <p className="text-sm text-foreground/80 leading-relaxed mb-8 border-l-2 border-primary/50 pl-3">
              This programme operates directly within PhysAnalytica&apos;s core physiotherapy mission of helping you move better with less pain, underpinned by the combined strengths of clinical physiotherapy, biomechanics, physiology and targeted fitness testing.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto">
              <Button asChild size="lg" className="shadow-sm font-medium">
                <Link to="#assessment-pathway" className="flex items-center justify-center gap-2">
                  Explore Your Assessment
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent">
                <Link to="/BookNowPage" className="flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-4 text-muted-foreground" /></SafeHydrate>
                  Book or Enquire
                </Link>
              </Button>
            </div>
          </div>

          {/* Imagery block depicting active adults across broad age brackets */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/active_adults_fitness_biomechanics_longevity.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/active_adults_fitness_biomechanics_longevity.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/active_adults_fitness_biomechanics_longevity.png-wvc-sizes" alt="Active healthy adults across different ages undergoing movement and fitness analysis in a clinical setting" loading="eager" decoding="async" className="w-full h-[380px] sm:h-[460px] lg:h-[500px] object-cover object-center" />

              {/* Sub-panel highlighting multi-modal testing */}
              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-card/95 backdrop-blur-md border border-border shadow-xs">
                <div className="flex items-center gap-2 text-xs font-semibold text-primary mb-1 uppercase tracking-wider">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  Clinical & Metabolic Rigour
                </div>
                <div className="text-xs sm:text-sm text-card-foreground">
                  Individualised physiological insights for everyday vitality, injury prevention, and lifelong motion.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}